import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { cn } from "@/lib/utils"
import { Check, CircleAlert, Info } from "lucide-react"
import type { ReactNode } from "react"
import { useEffect, useRef, useState } from "react"

/** How long the dialog stays open before it dismisses itself. */
export const AUTO_DISMISS_MS = 5_000
/** Countdown resolution, also the transition step of the draining bar. */
const TICK_MS = 100

export interface AutoDismissDialogProps {
  title: string
  /** What the badge above the title says happened. "error" swaps the green check
   *  for a red alert: this shell is the success moment by default, and a green
   *  check above the words "Import failed" reads as a contradiction. */
  tone?: "success" | "info" | "error"
  description?: ReactNode
  children?: ReactNode
  /**
   * Dismisses on its own after `AUTO_DISMISS_MS`, visualised by the draining
   * bar at the bottom; hovering pauses the countdown. `hold` switches the
   * countdown off entirely (the dialog then stays until dismissed), for
   * content that must not vanish unread.
   */
  hold?: boolean
  /** Fired exactly once: countdown ran out, or a footer action (and, with no
   *  `onClose` wired, Esc/backdrop/✕ too). */
  onDone: () => void
  /**
   * SPLITS "CLOSE THE DIALOG" OFF FROM "DO THE DONE ACTION". Without it every
   * way out — the ✕ in the corner, Esc, the backdrop — runs `onDone`, so
   * dismissing the declaration-approval result navigated to the list exactly
   * like its "Zur Übersicht" button did (product-owner feedback, 03.09.: the
   * ✕ should only close the window). When given, those three CLOSE the dialog
   * and call this instead; the countdown and the footer action keep running
   * `onDone`. Closing also stops the countdown for good — a timer left
   * running behind a dismissed dialog would navigate a beat later anyway.
   */
  onClose?: () => void
  /** Footer slot; receives the guarded dismiss so a button can close early.
   *  Stays in place under the body, which is the only part that scrolls.
   *  Spaces its own actions, so a caller may pass several buttons as a bare
   *  fragment. */
  footer?: (dismiss: () => void) => ReactNode
  contentClassName?: string
}

/**
 * Shared success-moment shell: check mark, title, optional description, a
 * content slot, a footer slot and the self-dismiss countdown. Used by the
 * master-data commit summary and the declaration approval so both read as
 * the same "done, moving on" beat without a mandatory click.
 */
export function AutoDismissDialog({
  title,
  tone = "success",
  description,
  children,
  hold = false,
  onDone,
  onClose,
  footer,
  contentClassName,
}: AutoDismissDialogProps) {
  const [remaining, setRemaining] = useState(AUTO_DISMISS_MS)
  const [paused, setPaused] = useState(false)
  const [closed, setClosed] = useState(false)
  const doneRef = useRef(false)

  const dismiss = () => {
    if (doneRef.current) return
    doneRef.current = true
    onDone()
  }

  // The ✕ / Esc / backdrop route, when the caller wired `onClose`: the dialog
  // goes away and nothing else happens. `doneRef` is set too, so the
  // countdown effect below can never fire `onDone` after the fact.
  const close = () => {
    if (doneRef.current) return
    doneRef.current = true
    setClosed(true)
    onClose?.()
  }

  useEffect(() => {
    if (hold || paused || closed) return undefined
    const id = setInterval(
      () => setRemaining((r) => Math.max(0, r - TICK_MS)),
      TICK_MS,
    )
    return () => clearInterval(id)
  }, [hold, paused, closed])

  useEffect(() => {
    if (remaining > 0 || doneRef.current) return
    doneRef.current = true
    onDone()
  }, [remaining, onDone])

  if (closed) return null

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) (onClose ? close : dismiss)()
      }}
    >
      <DialogContent
        className={cn(
          "flex max-h-[85vh] flex-col gap-0 overflow-hidden p-0",
          contentClassName,
        )}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        {/* The only scrolling region. The footer and the countdown bar sit
            outside it: inside, a long body (the approval result with its
            verdict matrix open) scrolled the actions out of the dialog.
            `min-h-0` lets this flex child shrink below its content, without
            it nothing scrolls and the dialog outgrows its max height. The
            column lives one level further in, so its items keep their size
            and scroll: as flex items of the capped region itself they
            shrank instead (the round badge turned into a flat pill). */}
        <div
          data-testid="auto-dismiss-body"
          className="min-h-0 flex-1 overflow-y-auto"
        >
          <div className="flex flex-col items-center gap-6 px-8 pt-8 pb-6">
            <div
              className={cn(
                "flex h-12 w-12 animate-in items-center justify-center rounded-full duration-300 zoom-in-50 fade-in motion-reduce:animate-none",
                tone === "error" && "bg-red1/10 text-red1",
                tone === "info" && "bg-grey5/10 text-grey5",
                tone === "success" && "bg-success/10 text-success",
              )}
            >
              {tone === "success" ? (
                <Check className="h-5 w-5" />
              ) : tone === "info" ? (
                <Info className="h-5 w-5" />
              ) : (
                <CircleAlert className="h-5 w-5" />
              )}
            </div>

            <DialogHeader className="items-center gap-1 text-center sm:text-center">
              <DialogTitle className="text-base font-normal text-foreground">
                {title}
              </DialogTitle>
              {description && (
                <DialogDescription className="text-sm text-muted-foreground">
                  {description}
                </DialogDescription>
              )}
            </DialogHeader>

            {children}
          </div>
        </div>

        {footer && (
          <div className="flex w-full shrink-0 items-center justify-end gap-2 border-t border-grey4/60 px-8 py-4">
            {footer(dismiss)}
          </div>
        )}

        {!hold && (
          <div aria-hidden="true" className="h-1 w-full shrink-0 bg-grey4/40">
            <div
              data-testid="autoclose-bar"
              className="h-full bg-yellow1"
              style={{
                width: `${(remaining / AUTO_DISMISS_MS) * 100}%`,
                transition: `width ${TICK_MS}ms linear`,
              }}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
